import { useState } from 'react';
import { Segmented } from 'antd';
import { AreaChartOutlined, DashboardOutlined, QuestionCircleOutlined, ReloadOutlined } from '@ant-design/icons';

import { Bento, PageHeader, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Button } from '../../../components/ui';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { soVN } from '../../../lib/dinh-dang';
import { DaiLoi } from '../components/chung/VungDuLieu';
import { ChuaDoDuoc, KhungCho, TheoNguon, TheoVai, ThongSo, TocDo, TroChuyen } from '../components/van-hanh/SuDung';
import { useSuDung } from '../hooks/useVanHanh';
import { chieuCaoCot, coNhanGio, ngayNgan, thuCuaNgay } from '../nghiep-vu/van-hanh';

const NGAY = 7;

type CheDoXuHuong = 'ngay' | 'gio';

const KHOA_CHE_DO = 'foxai.tro_ly_ai.tong_quan.xu_huong';

function docCheDo(): CheDoXuHuong {
  try {
    return window.localStorage.getItem(KHOA_CHE_DO) === 'gio' ? 'gio' : 'ngay';
  } catch {
    return 'ngay';
  }
}

interface Cot {
  khoa: string;
  n: number;
  /** Nhãn chính dưới cột. Rỗng thì cột vẫn đứng đó, chỉ không ghi chữ. */
  nhan: string;
  phu?: string;
  /** Chú thích khi rê chuột — luôn đủ, kể cả khi nhãn bị thưa bớt. */
  goiY: string;
}

/**
 * Dãy cột của "xu hướng hỏi" — `div` cao theo tỉ lệ chứ không kéo một thư viện biểu đồ: đây là một dãy số không âm.
 * Ngày/giờ KHÔNG có lượt nào vẫn có chỗ đứng (cột rỗng): bỏ khoảng trống thì biểu đồ đọc thành "vẫn đều".
 */
function DayCot({ cot, day }: { cot: Cot[]; day: boolean }) {
  const dinh = Math.max(1, ...cot.map((c) => c.n));
  return (
    <div className={cn('flex h-44 items-end', day ? 'gap-1' : 'gap-2')}>
      {cot.map((c) => (
        <div key={c.khoa} className="group flex min-w-0 flex-1 flex-col items-center gap-1.5" title={c.goiY}>
          <span
            className={cn('text-xs font-medium tabular-nums text-content-secondary', day && 'hidden xl:block')}
          >
            {c.n || ''}
          </span>
          <div className="flex w-full flex-1 items-end">
            <div
              className={cn(
                'w-full rounded-t transition-colors',
                c.n ? 'bg-primary-500 group-hover:bg-primary-600' : 'bg-subtle',
              )}
              // Chiều cao là DỮ LIỆU, không phải một bậc trong thang thiết kế — nên đi qua `style`.
              style={{ height: c.n ? `${chieuCaoCot(c.n, dinh)}%` : '3px' }}
              aria-hidden
            />
          </div>
          <span className="h-4 w-full truncate text-center text-xs tabular-nums text-content-muted">{c.nhan}</span>
          {c.phu ? (
            <span className="w-full truncate text-center text-xs tabular-nums text-content-muted">{c.phu}</span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function XuHuong({
  theoNgay, theoGio,
}: {
  theoNgay: { ngay: string; n: number }[];
  /** Vắng khi máy chủ cũ chưa gửi — khi ấy công tắc ẩn đi thay vì mở ra một biểu đồ trống. */
  theoGio?: { gio: number; n: number }[];
}) {
  const { t, locale } = useT();
  const [cheDo, setCheDo] = useState<CheDoXuHuong>(docCheDo);
  const theoGioCo = theoGio !== undefined && theoGio.length > 0;
  const gio = cheDo === 'gio' && theoGioCo;

  const cot: Cot[] = gio
    ? theoGio.map((d) => ({
        khoa: String(d.gio),
        n: d.n,
        nhan: coNhanGio(d.gio) ? t('troLyAiVanHanhTongQuan.nhanGio', { gio: d.gio }) : '',
        goiY: t('troLyAiVanHanhTongQuan.goiYGio', { tu: d.gio, den: d.gio + 1, n: soVN(d.n, locale) }),
      }))
    : theoNgay.map((d) => {
        const thu = thuCuaNgay(d.ngay);
        return {
          khoa: d.ngay,
          n: d.n,
          nhan: thu === null ? '' : t(`troLyAiVanHanhTongQuan.thu${thu}` as KhoaDich),
          phu: ngayNgan(d.ngay),
          goiY: t('troLyAiVanHanhTongQuan.goiYNgay', { ngay: ngayNgan(d.ngay), n: soVN(d.n, locale) }),
        };
      });

  const doiCheDo = (v: CheDoXuHuong) => {
    setCheDo(v);
    try {
      window.localStorage.setItem(KHOA_CHE_DO, v);
    } catch {
      // Chế độ riêng tư chặn ghi — mất phần ghi nhớ, không mất tính năng.
    }
  };

  return (
    <Tile span="lg:col-span-8">
      <TileHeader
        icon={<AreaChartOutlined />}
        tone="primary"
        title={t('troLyAiVanHanhTongQuan.xuHuong')}
        mo={gio ? t('troLyAiVanHanhTongQuan.xuHuongGioMo', { n: NGAY }) : t('troLyAiVanHanhTongQuan.xuHuongMo')}
        action={
          theoGioCo ? (
            <Segmented<CheDoXuHuong>
              size="small"
              aria-label={t('troLyAiVanHanhTongQuan.cheDoXuHuong')}
              value={gio ? 'gio' : 'ngay'}
              onChange={doiCheDo}
              options={[
                { value: 'ngay', label: t('troLyAiVanHanhTongQuan.theoNgay') },
                { value: 'gio', label: t('troLyAiVanHanhTongQuan.theoGio') },
              ]}
            />
          ) : undefined
        }
      />
      <TileBody>
        {cot.length === 0 ? (
          <p className="py-8 text-center text-sm text-content-muted">{t('troLyAiVanHanhTongQuan.chuaCoLuot')}</p>
        ) : (
          <DayCot cot={cot} day={gio} />
        )}
      </TileBody>
    </Tile>
  );
}

/**
 * Câu hay được hỏi nhất. Máy chủ đã gom biến thể (hoa/thường, khoảng trắng, dấu câu cuối) TRƯỚC `LIMIT` — lấy 20 dòng
 * thô rồi gộp ở trình duyệt cho ra một top-20 sai.
 */
function CauThuongGap({ cau }: { cau: { cau: string; n: number }[] }) {
  const { t, locale } = useT();
  return (
    <Tile span="lg:col-span-6">
      <TileHeader
        icon={<QuestionCircleOutlined />}
        tone="sky"
        title={t('troLyAiVanHanhTongQuan.cauThuongGap')}
        mo={t('troLyAiVanHanhTongQuan.cauThuongGapMo')}
      />
      <TileBody>
        {cau.length === 0 ? (
          <p className="py-2 text-sm text-content-muted">{t('troLyAiVanHanhTongQuan.chuaCoCau')}</p>
        ) : (
          <ol className="space-y-2">
            {cau.slice(0, 8).map((c, i) => (
              <li key={`${i}-${c.cau}`} className="flex items-start gap-3">
                <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-subtle text-xs font-medium tabular-nums text-content-secondary">
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1 text-sm leading-snug text-content-primary">{c.cau}</span>
                <span className="shrink-0 text-sm font-semibold tabular-nums text-content-secondary">
                  {soVN(c.n, locale)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </TileBody>
    </Tile>
  );
}

/**
 * Tổng quan Trợ lý AI (`/tro-ly-ai/tong-quan`, port `DashboardView` native): một lời gọi `GET /su-dung?ngay=7` cho cả
 * màn, các khối đọc chung payload.
 */
export function TongQuanTroLyView() {
  const { t } = useT();
  const q = useSuDung(NGAY);

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.troLiAi') }, { label: t('nav.muc.tongQuan') }]}
          titleIcon={<DashboardOutlined className="text-primary-600" />}
          title={t('troLyAiVanHanhTongQuan.tieuDe')}
          subtitle={t('troLyAiVanHanhTongQuan.mo', { n: NGAY })}
          actions={
            <Button
              variant="ghost"
              size="md"
              title={t('chung.lamMoi')}
              aria-label={t('chung.lamMoi')}
              onClick={() => void q.refetch()}
            >
              <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
            </Button>
          }
        />

        <Bento>
          {q.isPending ? (
            <KhungCho />
          ) : q.error ? (
            // Nói ra lỗi thay vì khối rỗng: "chưa ai hỏi câu nào" và "không đọc được số liệu" trông giống hệt nhau.
            <Tile>
              <DaiLoi loi={q.error} onThuLai={() => void q.refetch()} />
            </Tile>
          ) : q.data ? (
            <>
              <ThongSo d={q.data} />
              <XuHuong theoNgay={q.data.theoNgay} theoGio={q.data.theoGio} />
              <TocDo d={q.data} span="lg:col-span-4" />
              <CauThuongGap cau={q.data.cauThuongGap ?? []} />
              <TheoNguon d={q.data} span="lg:col-span-6" />
              <TheoVai d={q.data} span="lg:col-span-6" />
              <TroChuyen d={q.data} span="lg:col-span-6" />
              <ChuaDoDuoc d={q.data} />
            </>
          ) : null}
        </Bento>
      </div>
    </div>
  );
}
